import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Crown, Pencil, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader } from "@/components/Loader";
import { ThemeSwatch } from "@/components/ThemeSwatch";
import { ThemeEditor } from "@/components/ThemeEditor";
import { SceneBreakCard } from "@/components/SceneBreakCard";
import { ProLockDialog } from "@/components/ProLockDialog";
import { useAppearance } from "@/context/AppearanceContext";
import { formatApiError } from "@/lib/api";
import { PRESETS } from "@/lib/theme";

const Section = ({ eyebrow, title, note, children, testId }) => (
  <section className="space-y-5" data-testid={testId}>
    <div><p className="font-code text-[10px] uppercase tracking-[0.3em] text-amber-500/80">{eyebrow}</p>
      <h2 className="font-display text-3xl text-amber-100 mt-1">{title}</h2>
      {note && <p className="text-sm text-slate-400 mt-1 max-w-2xl">{note}</p>}</div>
    {children}
  </section>
);

const IconBtn = ({ onClick, title, icon: Icon, testId, danger }) => (
  <button onClick={onClick} title={title} className={`p-1.5 rounded-full text-slate-500 transition-colors ${danger ? "hover:text-rose-400" : "hover:text-amber-300"}`} data-testid={testId}><Icon className="w-3.5 h-3.5" /></button>
);

export default function AppearancePage() {
  const nav = useNavigate();
  const { app, active, setTheme, setSceneBreak, deleteTheme } = useAppearance();
  const [lock, setLock] = useState(null);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);
  if (!app) return <Loader />;
  const pro = app.is_pro;
  const run = (p) => p.catch((e) => toast.error(formatApiError(e)));
  const pick = (id, free) => (free || pro ? run(setTheme(id)) : setLock("Themes"));
  const open = (initial) => { setEditing(initial); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async () => { await run(deleteTheme(del.id)); toast.success(`"${del.name}" deleted`); setDel(null); };

  return (
    <div className="relative z-10 min-h-screen px-6 md:px-12 py-10 max-w-6xl mx-auto space-y-14" data-testid="appearance-page">
      <div>
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 mb-8" data-testid="appearance-back"><ArrowLeft className="w-4 h-4" /> Back</button>
        <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-3">Settings</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50">Appearance</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-xl">Your theme covers your library, your worlds and the writing desk. Only you see it.</p>
      </div>

      {editing && <ThemeEditor key={editing.key} initial={editing} onDone={() => setEditing(null)} />}

      <Section eyebrow="Presets" title="Themes" testId="presets-section"
        note={pro ? "Pick one, or customize any preset to start your own." : "Lorely Dark and Lorely Light are free. The rest come with Pro."}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {PRESETS.map((p) => (
            <ThemeSwatch key={p.id} v={p.values} name={p.name} active={!editing && app.theme_id === p.id} locked={!p.free && !pro}
              onClick={() => pick(p.id, p.free)} testId={`preset-${p.id}`}>
              {pro && <IconBtn onClick={() => open({ key: `p-${p.id}-${Date.now()}`, name: `My ${p.name}`, values: p.values })} title="Customize" icon={Wand2} testId={`preset-${p.id}-customize`} />}
            </ThemeSwatch>
          ))}
        </div>
      </Section>

      <Section eyebrow="Yours" title="Your themes" testId="custom-section"
        note="Choose every colour, the heading and body fonts, how round the corners are and whether the paper texture shows.">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {app.themes.map((t) => (
            <ThemeSwatch key={t.id} v={t.values} name={t.name} active={!editing && app.theme_id === t.id} locked={!pro} onClick={() => pick(t.id, false)} testId={`custom-theme-${t.id}`}>
              {pro && <IconBtn onClick={() => open({ ...t, key: `e-${t.id}-${Date.now()}` })} title="Edit" icon={Pencil} testId={`custom-theme-${t.id}-edit`} />}
              <IconBtn onClick={() => setDel(t)} title="Delete" icon={Trash2} danger testId={`custom-theme-${t.id}-delete`} />
            </ThemeSwatch>
          ))}
          <button onClick={() => (pro ? open({ key: `n-${Date.now()}`, name: "", values: active }) : setLock("Custom themes"))}
            className="min-h-[10.5rem] rounded-2xl border border-dashed border-line hover:border-amber-500/50 text-slate-400 hover:text-amber-300 flex flex-col items-center justify-center gap-2 transition-colors" data-testid="new-theme-btn">
            {pro ? <Plus className="w-5 h-5" /> : <Crown className="w-5 h-5" />}
            <span className="text-sm">{pro ? "New theme" : "Make your own with Pro"}</span>
          </button>
        </div>
      </Section>

      <Section eyebrow="Writing desk" title="Scene breaks" testId="scene-breaks-section"
        note="How a scene break looks in your chapters. New works use this; each work can choose its own in Details.">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {app.scene_breaks.filter((d) => d.active || d.id === app.scene_break).map((d) => (
            <SceneBreakCard key={d.id} d={d} active={app.scene_break === d.id} locked={d.pro && !pro}
              onClick={() => (!d.pro || pro ? run(setSceneBreak(d.id)) : setLock("Scene break designs"))} />
          ))}
        </div>
      </Section>

      <ProLockDialog open={!!lock} onOpenChange={(o) => !o && setLock(null)} what={lock || "Themes"} />
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-panel border-line" data-testid="theme-delete-dialog">
          <AlertDialogTitle className="font-display text-2xl text-amber-50">Delete "{del?.name}"?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">If it's your current theme, you'll switch back to Lorely Dark.</AlertDialogDescription>
          <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="theme-delete-confirm">Delete theme</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
